import { useState, useEffect, useCallback, useRef } from 'react';
import { useWebRTC } from '../context/WebRTCContext';

/**
 * useAutonomyConsole — binding for the Autonomous-mode console.
 *
 * Loads the director's goal/setpoints + the learning engine's intervention
 * effectiveness, and maintains a live feed of what the AI is doing autonomously
 * (advisor.executed) and what the arbiter throttled (advisor.suppressed).
 */
export function useAutonomyConsole() {
    const { isConnected, featureManager } = useWebRTC();
    const [goal, setGoal] = useState(null);
    const [effectiveness, setEffectiveness] = useState([]);
    const [activity, setActivity] = useState([]);
    const [config, setConfig] = useState(null);
    const [activityLog, setActivityLog] = useState([]);
    const [status, setStatus] = useState(null);     // { presence, arbiter }
    const [analytics, setAnalytics] = useState(null);
    const [deliberations, setDeliberations] = useState([]);
    const [scheduledEvents, setScheduledEvents] = useState([]);
    const featureRef = useRef(null);

    const loadScheduled = useCallback(async () => {
        const f = featureRef.current || featureManager?.getFeature?.('sacred-timeline');
        if (!f?.fetchScheduled) return;
        try { setScheduledEvents(await f.fetchScheduled()); }
        catch (err) { console.warn('[useAutonomyConsole] scheduled failed:', err.message); }
    }, [featureManager]);

    const loadActivityLog = useCallback(async () => {
        const f = featureRef.current || featureManager?.getFeature?.('sacred-timeline');
        if (!f?.getActivityLog) return;
        try { setActivityLog(await f.getActivityLog(50)); }
        catch (err) { console.warn('[useAutonomyConsole] activity log failed:', err.message); }
    }, [featureManager]);

    const loadStatus = useCallback(async () => {
        const f = featureRef.current || featureManager?.getFeature?.('sacred-timeline');
        if (!f?.getAutonomyStatus) return;
        try { setStatus(await f.getAutonomyStatus()); }
        catch (err) { console.warn('[useAutonomyConsole] status failed:', err.message); }
    }, [featureManager]);

    const loadAnalytics = useCallback(async () => {
        const f = featureRef.current || featureManager?.getFeature?.('sacred-timeline');
        if (!f?.getAutonomyAnalytics) return;
        try { setAnalytics(await f.getAutonomyAnalytics()); }
        catch (err) { console.warn('[useAutonomyConsole] analytics failed:', err.message); }
    }, [featureManager]);

    const compileGoal = useCallback(async (text) => {
        const f = featureRef.current;
        if (!f?.compileGoal) return { ok: false, error: 'not connected' };
        try { return await f.compileGoal(text); }
        catch (err) { return { ok: false, error: err.message }; }
    }, []);

    const cancelScheduled = useCallback(async (id) => {
        const f = featureRef.current;
        if (!f?.cancelAction) return;
        try { await f.cancelAction(id); } finally { loadScheduled(); }
    }, [loadScheduled]);

    const runScheduledNow = useCallback(async (id) => {
        const f = featureRef.current;
        if (!f?.executeNow) return;
        try { await f.executeNow(id); } finally { loadScheduled(); loadActivityLog(); }
    }, [loadScheduled, loadActivityLog]);

    const refresh = useCallback(async () => {
        const f = featureManager?.getFeature?.('sacred-timeline');
        if (!f?.getAutonomyGoal) return;
        try {
            const r = await f.getAutonomyGoal();
            setGoal(r.goal);
            setEffectiveness(r.effectiveness || []);
        } catch (err) {
            console.warn('[useAutonomyConsole] refresh failed:', err.message);
        }
        if (f.getAutonomyConfig) {
            try {
                const cfg = await f.getAutonomyConfig();
                if (cfg) setConfig(cfg);
            } catch (err) {
                console.warn('[useAutonomyConsole] config refresh failed:', err.message);
            }
        }
        loadActivityLog();
        loadStatus();
        loadAnalytics();
        loadScheduled();
    }, [featureManager, loadActivityLog, loadStatus, loadAnalytics, loadScheduled]);

    useEffect(() => {
        if (!isConnected || !featureManager) return;
        const f = featureManager.getFeature('sacred-timeline');
        if (!f) return;
        featureRef.current = f;
        refresh();
        const unsub = f.onAdvisorEvent?.((type, p) => {
            if (type === 'advisor.executed' || type === 'advisor.suppressed') {
                setActivity((prev) => [{ kind: type, ...p, _t: Date.now() }, ...prev].slice(0, 30));
            }
            // Executions and reverts change the persisted log — refresh it.
            if (type === 'advisor.executed' || type === 'advisor.reverted') { loadActivityLog(); loadScheduled(); }
            // Continuous reasoning stream (incl. "no action").
            if (type === 'policy.deliberated') {
                setDeliberations((prev) => [{ ...p, _t: Date.now() }, ...prev].slice(0, 25));
                if (p.plan) setStatus((s) => (s ? { ...s, presence: { ...s.presence, plan: p.plan } } : s));
            }
        });
        return () => unsub?.();
    }, [isConnected, featureManager, refresh, loadActivityLog, loadScheduled]);

    const saveGoal = useCallback(async (next) => {
        const f = featureRef.current;
        if (!f) return;
        setGoal(next); // optimistic
        try { await f.setAutonomyGoal(next); } catch { refresh(); }
    }, [refresh]);

    /**
     * Persist a partial config (deep-merged on the desktop). Applies the same
     * merge optimistically so the UI reflects the change immediately.
     */
    const saveConfig = useCallback(async (patch) => {
        const f = featureRef.current;
        if (!f?.setAutonomyConfig) return;
        setConfig((prev) => (prev ? deepMergeLocal(prev, patch) : prev));
        try {
            const res = await f.setAutonomyConfig(patch);
            if (res?.config) setConfig(res.config);
        } catch {
            refresh();
        }
    }, [refresh]);

    const undo = useCallback(async (id) => {
        const f = featureRef.current;
        if (!f?.undoAction) return;
        try { await f.undoAction(id); } finally { loadActivityLog(); }
    }, [loadActivityLog]);

    const simulate = useCallback(async () => {
        const f = featureRef.current;
        if (!f?.simulatePolicy) return { success: false, error: 'not connected' };
        try { return await f.simulatePolicy(); }
        catch (err) { return { success: false, error: err.message }; }
    }, []);

    /** Force the AI to decide its next move NOW and put it in the approval inbox
     *  (shuffle → a different move than what's already pending). Refreshes status
     *  so the new pending proposal + presence show immediately. */
    const propose = useCallback(async ({ shuffle = false } = {}) => {
        const f = featureRef.current;
        if (!f?.proposeNextMove) return { success: false, error: 'not connected' };
        try {
            const res = await f.proposeNextMove({ shuffle });
            loadStatus();
            return res;
        } catch (err) { return { success: false, error: err.message }; }
    }, [loadStatus]);

    return {
        goal, effectiveness, activity, config, activityLog, status, analytics,
        deliberations, scheduledEvents,
        saveGoal, saveConfig, refresh, loadActivityLog, loadStatus, loadAnalytics, loadScheduled,
        undo, simulate, propose, compileGoal, cancelScheduled, runScheduledNow
    };
}

/** Local mirror of the desktop deep-merge for optimistic updates. */
function deepMergeLocal(base, override) {
    if (!override || typeof override !== 'object' || Array.isArray(override)) return base;
    const out = Array.isArray(base) ? [...base] : { ...base };
    for (const [k, v] of Object.entries(override)) {
        if (v && typeof v === 'object' && !Array.isArray(v) &&
            out[k] && typeof out[k] === 'object' && !Array.isArray(out[k])) {
            out[k] = deepMergeLocal(out[k], v);
        } else {
            out[k] = v;
        }
    }
    return out;
}
